import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import {
  Gift,
  ChevronLeft,
  Copy,
  Share2,
  Users,
  Award,
  CheckCircle,
  Clock,
  Loader2,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';
import { copyText } from '@/lib/copyText';

export default function ReferralPage() {
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);

  const fetchStats = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/referrals/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const copyCode = () => {
    if (stats?.referral_code) {
      copyText(stats.referral_code, 'Referral code copied!');
    }
  };

  const shareCode = async () => {
    if (navigator.share && stats?.referral_code) {
      try {
        await navigator.share({
          title: 'Join Chain29',
          text: `Use my referral code ${stats.referral_code} to get 50 bonus points when you sign up!`,
          url: window.location.origin
        });
      } catch (err) {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/referrals/invite`, { email: inviteEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="referral-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Invite Friends</h1>
            <p className="text-sm text-muted-foreground">
              Earn rewards when friends sign up
            </p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-8 w-1/2 mb-4" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Referral Code Card */}
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <Gift className="h-12 w-12 mx-auto text-primary mb-2" />
                  <h2 className="text-lg font-semibold">Your Referral Code</h2>
                  <p className="text-sm text-muted-foreground">
                    Share this code and you both earn points!
                  </p>
                </div>

                <div className="flex items-center gap-2 bg-background rounded-lg p-3 mb-4">
                  <code className="flex-1 text-2xl font-mono font-bold text-center tracking-wider">
                    {stats?.referral_code}
                  </code>
                  <Button variant="ghost" size="icon" onClick={copyCode}>
                    <Copy className="h-5 w-5" />
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button onClick={copyCode} variant="outline" className="flex-1">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </Button>
                  <Button onClick={shareCode} className="flex-1">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* How it Works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How It Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Share your code</p>
                    <p className="text-sm text-muted-foreground">
                      Send your unique referral code to friends
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Friend signs up</p>
                    <p className="text-sm text-muted-foreground">
                      They create an account using your code
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">3</span>
                  </div>
                  <div>
                    <p className="font-medium">You both earn points!</p>
                    <p className="text-sm text-muted-foreground">
                      You get <strong>100 points</strong>, they get <strong>50 points</strong>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Send Invite */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Send Invite</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={sendInvite} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="friend@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={sending || !inviteEmail.trim()}>
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 rounded-lg">
                    <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
                    <p className="text-2xl font-bold">{stats?.completed_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                  <div className="text-center p-3 bg-primary/10 rounded-lg">
                    <Award className="h-5 w-5 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">{stats?.total_points_earned || 0}</p>
                    <p className="text-xs text-muted-foreground">Points Earned</p>
                  </div>
                </div>

                {stats?.referrals?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Recent Invites</h4>
                    {stats.referrals.slice(0, 5).map((ref) => (
                      <div key={ref.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <span className="text-sm">{ref.referred_email}</span>
                        <Badge variant={ref.status === 'completed' ? 'default' : 'secondary'}>
                          {ref.status === 'completed' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Completed</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
